import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { SERVICE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  full_name: "",
  phone: "",
  email: "",
  postcode: "",
  service: "",
  property_type: "",
  message: "",
};

export const QuoteForm = ({ defaultService = "" }) => {
  const [form, setForm] = useState({ ...empty, service: defaultService });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.service || !form.property_type) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quote`, form);
      setDone(true);
      toast.success(data.message || "Thank you. Your enquiry has been received.");
      setForm({ ...empty, service: defaultService });
    } catch (err) {
      toast.error("Something went wrong. Please call us or try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-success"
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center"
      >
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" strokeWidth={1.5} />
        <h3 className="mt-4 font-display text-2xl font-semibold text-brand-blue">Enquiry received</h3>
        <p className="mt-2 text-slate-600">
          Thank you. Your enquiry has been received and our team will contact you shortly.
        </p>
        <button
          onClick={() => setDone(false)}
          data-testid="quote-reset"
          className="mt-6 text-sm font-medium text-brand-electric hover:underline"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-[15px] text-brand-blue outline-none transition focus:border-brand-electric focus:bg-white focus:ring-1 focus:ring-brand-electric";
  const labelCls = "mb-1.5 block text-sm font-semibold text-slate-700";

  return (
    <form onSubmit={submit} data-testid="quote-form" className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input className={inputCls} data-testid="qf-name" value={form.full_name} onChange={update("full_name")} placeholder="Your name" />
        </div>
        <div>
          <label className={labelCls}>Phone Number *</label>
          <input className={inputCls} data-testid="qf-phone" value={form.phone} onChange={update("phone")} placeholder="04XX XXX XXX" />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Email Address *</label>
          <input type="email" className={inputCls} data-testid="qf-email" value={form.email} onChange={update("email")} placeholder="you@email.com" />
        </div>
        <div>
          <label className={labelCls}>Postcode</label>
          <input className={inputCls} data-testid="qf-postcode" value={form.postcode} onChange={update("postcode")} placeholder="e.g. 3550" />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Service Required *</label>
          <select className={inputCls} data-testid="qf-service" value={form.service} onChange={update("service")}>
            <option value="">Select a service</option>
            {SERVICE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Property Type *</label>
          <select className={inputCls} data-testid="qf-property" value={form.property_type} onChange={update("property_type")}>
            <option value="">Select property type</option>
            <option value="Residential">Residential</option>
            <option value="Commercial">Commercial</option>
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Message</label>
        <textarea rows={4} className={inputCls} data-testid="qf-message" value={form.message} onChange={update("message")} placeholder="Tell us a little about your project..." />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="qf-submit"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B4F8A] px-8 py-4 font-medium text-white shadow-[0_6px_18px_rgba(16,42,67,0.20)] transition-colors duration-300 hover:bg-[#18558A] disabled:opacity-70"
      >
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : "Request Free Quote"}
      </button>
    </form>
  );
};
